import React, { useState, useEffect, useRef } from 'react';
import { Text as UIText } from './ui';

const FloatingChefTab = ({ 
  isVisible = false,
  isExpanded = false,
  onToggle,
  onNext,
  onBack,
  message = "",
  subMessage = "",
  chefState = 'talking',
  position = 'right',
  showNextButton = false,
  showBackButton = false,
  hideChefImage = false, // New prop to hide chef image when used with existing FloatingChef
  className = ""
}) => {
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [tabPosition, setTabPosition] = useState(() => {
    // Load saved position from localStorage
    const saved = localStorage.getItem('chefTabPosition');
    return saved ? JSON.parse(saved) : { edge: 'bottom', offset: 24 };
  });
  const buttonRef = useRef(null);

  useEffect(() => {
    if (isVisible && message) {
      // Clear text immediately when message changes
      setDisplayText('');
      setIsTyping(true);
      
      let currentIndex = 0;
      const typeInterval = setInterval(() => {
        if (currentIndex < message.length) {
          setDisplayText(message.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          setIsTyping(false);
          clearInterval(typeInterval);
        }
      }, 30);

      return () => clearInterval(typeInterval);
    }
  }, [message]); // Remove isVisible dependency so text clears immediately when message changes

  const getChefImage = () => {
    // For walkthrough, always use talking chef
    return '/images/Chef-talking.png';
  };


  if (!isVisible) return null;

  // Get dynamic positioning based on position prop and chef tab position
  const getDynamicStyle = () => {
    // If hideChefImage is true, position relative to existing FloatingChef button
    if (hideChefImage) {
      // Get the chef button position from localStorage
      const saved = localStorage.getItem('chefTabPosition');
      const chefPosition = saved ? JSON.parse(saved) : { edge: 'bottom', offset: 24 };
      const bodyPadding = 6; // var(--spacing-sm) = 6px
      const buttonSize = 88;
      
      if (chefPosition.edge === 'left') {
        // Chef is on left edge, position speech bubble to the right of it
        // Account for body padding and button width
        const leftPosition = bodyPadding + buttonSize + 20;
        return {
          position: 'fixed',
          left: `${leftPosition}px`,
          top: `${chefPosition.offset}px`,
          zIndex: 1000001, // Higher than fork modal (1000000)
          transform: 'translateY(-50%)'
        };
      } else {
        // Chef is on bottom edge, position speech bubble above it
        // Account for body padding
        const bottomPosition = bodyPadding + buttonSize + 20;
        return {
          position: 'fixed',
          bottom: `${bottomPosition}px`,
          left: `${chefPosition.offset}px`,
          zIndex: 1000001, // Higher than fork modal (1000000)
          transform: 'translateX(-50%)'
        };
      }
    }
    
    // Original positioning for standalone mode
    const baseStyle = {
      position: 'fixed',
      margin: '0',
      zIndex: 1000001, // Higher than fork modal (1000000)
      cursor: 'grab'
    };

    switch (position) {
      case 'bottom':
        return {
          ...baseStyle,
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)'
        };
      case 'left':
        return {
          ...baseStyle,
          left: '20px',
          bottom: '20px'
        };
      case 'right':
      default:
        return {
          ...baseStyle,
          right: '20px',
          bottom: '20px'
        };
    }
  };

  return (
    <div 
      ref={buttonRef}
      className="fixed"
      style={{
        ...getDynamicStyle(),
        zIndex: 1000001 // Higher than fork modal (1000000)
      }}
    >
      <div className={`flex items-end ${hideChefImage ? '' : 'flex-row-reverse'}`}>
        {/* Speech bubble */}
        {isExpanded && message && (
          <div className={`bg-neutral-0 hairline p-4 max-w-sm w-80 relative ${hideChefImage ? '' : 'mr-2 mb-16'}`} style={{ zIndex: 1000001 }}>
            {/* Speech bubble tail pointing to chef */}
            <div 
              className={`absolute w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-neutral-900 ${
                hideChefImage 
                  ? (position === 'left' ? 'bottom-0 right-8 transform translate-y-full' : 'bottom-0 left-8 transform translate-y-full')
                  : position === 'bottom' 
                  ? 'bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full' 
                  : position === 'left'
                  ? 'bottom-0 right-8 transform translate-y-full'
                  : 'bottom-0 left-8 transform translate-y-full'
              }`}
            ></div>
            
            <div className="flex items-start space-x-3">
              <div className="flex-1 min-w-0">
                <UIText size="sm" className="text-neutral-900 leading-relaxed break-words">
                  {displayText || message}
                  {isTyping && <span className="animate-pulse ml-1">|</span>}
                </UIText>
                {subMessage && !isTyping && (
                  <UIText size="xs" className="text-neutral-900 opacity-60 mt-1 break-words">
                    {subMessage}
                  </UIText>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {showBackButton && !isTyping && onBack && (
                  <button
                    onClick={onBack}
                    className="px-3 py-1 bg-neutral-50 text-neutral-900 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                  >
                    Back
                  </button>
                )}
                {showNextButton && !isTyping && onNext && (
                  <button
                    onClick={onNext}
                    className="px-3 py-1 bg-neutral-900 text-neutral-0 font-mono text-xs uppercase tracking-wider hover:opacity-60 transition-opacity"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Large Chef on bottom - only show if not hiding chef image */}
        {!hideChefImage && (
          <div className="relative">
            <img 
              src={getChefImage()} 
              alt="Chef Character" 
              className="w-20 h-20 object-contain cursor-pointer hover:opacity-60 transition-opacity"
              onClick={onToggle}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingChefTab;
